import { DependencyList, Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

export function useAsyncState<T>(
    func: () => Promise<T>,
    deps: DependencyList,
    initialValue: T
): [T, Dispatch<SetStateAction<T>>, () => Promise<void>] {
    const [data, setData] = useState(initialValue);

    const loadData = useCallback(async function loadData() {
        setData(await func());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    useEffect(() => {
        loadData();
    }, [loadData]);

    return [data, setData, loadData];
}

export function useAsyncStateWithIsLoading<T>(
    func: () => Promise<T>,
    deps: DependencyList,
    initialValue: T
): [T, Dispatch<SetStateAction<T>>, () => Promise<void>, boolean] {
    const [data, setData] = useState(initialValue);
    const loadingCounter = useRef(0);
    const [isLoading, setIsLoading] = useState(false);

    const loadData = useCallback(async function loadData() {
        loadingCounter.current = loadingCounter.current + 1;
        setIsLoading(loadingCounter.current > 0);
        try {
            const result = await func();
            setData(result);
        } catch (error) {
            console.error(error);
        } finally {
            loadingCounter.current = loadingCounter.current - 1;
            setIsLoading(loadingCounter.current > 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    useEffect(() => {
        loadData();
    }, [loadData]);

    return [data, setData, loadData, isLoading];
}

export function useLocalStorage<T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] {
    const readValue = useCallback(() => {
        try {
            const item = window.localStorage.getItem(key);
            return item ? JSON.parse(item) : initialValue;
        } catch (error) {
            console.warn(error);
            return initialValue;
        }
    }, [key, initialValue]);

    const [localState, setLocalState] = useState(readValue);
    const handleSetState = useCallback(
        (value) => {
            try {
                const nextState = typeof value === 'function' ? value(localState) : value;
                window.localStorage.setItem(key, JSON.stringify(nextState));
                setLocalState(nextState);
                window.dispatchEvent(new Event('local-storage'));
            } catch (e) {
                console.warn(e);
            }
        },
        [key, localState]
    );

    useEffect(() => {
        const onStorageChange = (event) => {
            if (event?.key && event.key !== key) {
                return;
            }

            setLocalState(readValue());
        };

        window.addEventListener('storage', onStorageChange);
        window.addEventListener('local-storage', onStorageChange);

        return () => {
            window.removeEventListener('storage', onStorageChange);
            window.removeEventListener('local-storage', onStorageChange);
        };
    }, []);

    return [localState, handleSetState];
}

export function useMergeState<T = { [key: string]: any }>(
    initialState: T
): [T, Dispatch<Partial<T>>, Dispatch<SetStateAction<T>>] {
    const [value, setValue] = useState(initialState);

    const mergeValue = useCallback((updates: Partial<T>) => setValue((oldValue) => ({ ...oldValue, ...updates })), []);

    return [value, mergeValue, setValue];
}